import { forwardRef, useCallback, useLayoutEffect, useRef, type InputHTMLAttributes } from 'react';

import {
  capRaw, caretAfterDigits, digitsBefore, group, roundRaw, separatorsFor, ungroup,
} from '../../lib/groupedNumber.js';
import { getCurrencyDecimals } from '../../lib/money.js';

/**
 * A number box that shows thousand separators while you type.
 *
 * OPT-IN BY DESIGN, and it must stay that way. The shared `Input` also renders
 * the phone box, the OTP box and the coupon field — grouping those would corrupt
 * them. Reach for this one only where the digits are a MAGNITUDE the server adds
 * or multiplies: money, quantities, stock counts. Never for a barcode, a code, a
 * phone number, a percentage or a millimetre.
 *
 * THE VALUE CONTRACT: `value` is always RAW ("1000") and `onValueChange` always
 * hands back RAW. Only the DOM ever sees "1,000". This is why the prop is not
 * called `onChange` — the wire schemas are strict (`/^-?\d+(\.\d{1,4})?$/`), so a
 * grouped string reaching the API is a hard 400, and a rename makes the compiler
 * find every call site rather than letting them keep compiling while silently
 * posting commas.
 *
 * `type` is deliberately absent: an <input type="number"> reports its value as ""
 * the moment it contains a separator, so this is always a text input with a
 * decimal inputMode (which still gets the numeric keypad on a phone).
 */
export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** RAW value, no separators. */
  value: string;
  /** Receives the RAW value — this is what you store and POST. */
  onValueChange: (raw: string) => void;
  invalid?: boolean;
  /**
   * Round to this currency's minor units on blur (IQD 0, USD 2). Omit for a
   * quantity, which has no currency and should not be padded.
   */
  currency?: string | null;
  /**
   * Keep up to this many decimals instead — round only past them, never pad. For
   * a UNIT PRICE: $0.2121 a piece is a real price, and rounding it to the
   * currency's cents on blur (0.21) moved the line total and the bill with it.
   * Wins over `currency`.
   */
  maxDecimals?: number;
  /** Locale for the separators. Defaults to the ASCII pair every latn locale uses. */
  locale?: string;
  /**
   * Show separators. Default true.
   *
   * Set false where the SAME box means a percentage depending on a sibling
   * select — a document discount, a coupon value, a discount rule. "15%" must
   * never render as a grouped amount. Everything else about the field is
   * unchanged, so the raw-value contract still holds either way.
   */
  grouped?: boolean;
  /**
   * Drop the built-in field styling and use only .
   *
   * For inputs that live INSIDE another control — a cart row sitting between its
   * ± buttons, say — where the border, height and padding belong to the wrapper.
   * Without this the caret logic would have to be copied to those call sites, and
   * subtle caret code kept in two places drifts.
   */
  unstyled?: boolean;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onValueChange, invalid, currency, maxDecimals, locale, grouped = true, unstyled, className, onBlur, onKeyDown, ...rest },
  ref,
) {
  const sep = separatorsFor(locale);
  // Ungrouped still routes through here so the raw contract, the sanitising and the
  // blur rounding are identical — only the separators are withheld.
  const display = grouped ? group(value ?? '', sep) : (value ?? '');
  const inner = useRef<HTMLInputElement | null>(null);
  /** Where the caret should sit after the next paint, in DIGITS not characters. */
  const wantDigits = useRef<number | null>(null);

  const setRef = useCallback((el: HTMLInputElement | null) => {
    inner.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
  }, [ref]);

  /**
   * Put the caret back after the same DIGIT it was after before reformatting.
   *
   * Character offsets move when a separator appears or disappears; the digit the
   * caret follows does not. Runs with no dependency array so it fires after every
   * commit — React may have rewritten the DOM value between our write and paint.
   */
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || wantDigits.current === null) return;
    const at = caretAfterDigits(el.value, wantDigits.current);
    wantDigits.current = null;
    // Only touch the selection while focused, or we steal the caret from
    // whatever the user moved to.
    if (document.activeElement === el) el.setSelectionRange(at, at);
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    wantDigits.current = digitsBefore(el.value, caret);
    onValueChange(ungroup(el.value, sep));
  };

  /**
   * Backspace onto a separator would otherwise be a dead key: it deletes the
   * comma, the value re-groups, and the comma comes straight back. Step over it
   * and delete the digit the person meant.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    onKeyDown?.(e);
    if (e.defaultPrevented || e.key !== 'Backspace') return;
    const el = e.currentTarget;
    const caret = el.selectionStart ?? 0;
    if (caret !== el.selectionEnd || caret === 0) return;
    if (el.value[caret - 1] !== sep.group) return;
    e.preventDefault();
    const next = el.value.slice(0, caret - 2) + el.value.slice(caret);
    wantDigits.current = digitsBefore(next, caret - 2);
    onValueChange(ungroup(next, sep));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    // Rounding belongs here, not on every keystroke — mid-type it would fight
    // somebody reaching for "1.05" the moment they passed "1.0".
    const decimals = currency ? getCurrencyDecimals(currency) : undefined;
    const rounded = maxDecimals !== undefined ? capRaw(value ?? '', maxDecimals) : roundRaw(value ?? '', decimals);
    if (rounded !== value) onValueChange(rounded);
    onBlur?.(e);
  };

  const cls = unstyled ? (className ?? '') : [
    'block h-10 w-full rounded-md border bg-bg px-3 text-sm text-fg outline-none transition',
    'placeholder:text-fg-muted',
    invalid
      ? 'border-danger focus:border-danger focus:ring-2 focus:ring-danger/20'
      : 'border-border focus:border-brand focus:ring-2 focus:ring-brand/20',
    'disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:opacity-60',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <input
      ref={setRef}
      // A grouped number is a left-to-right run. The admin runs RTL in Arabic,
      // and bidi reordering has already bitten this repo's printed receipts —
      // without this the separators land in the wrong place on screen.
      dir="ltr"
      inputMode="decimal"
      autoComplete="off"
      className={cls}
      aria-invalid={invalid || undefined}
      value={display}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      {...rest}
    />
  );
});
